import { SupportContext } from "./getSupportContext";

// What the payer was doing and what we know about it. Every field is optional
// because the portal fails at points where most of it does not exist yet --
// a search that returned nothing has no amount, no reference and no service.
export type SupportFacts = {
  // The business reference for the payment itself. It leads: it is the one an
  // agent can find a payment by, where the correlation reference below only
  // finds a failure. Absent whenever the payment has none that qualifies.
  paymentReference?: string;
  // The correlation reference of the failing request. This is the field that
  // makes the ticket joinable to the server's own log lines.
  reference?: string;
  code?: string;
  route?: string;
  locale?: string;
  serviceName?: string;
  // Ruled 2026-08-31: a ticket carries what telemetry may not. The payer
  // pressed a button asking us to look, so the matricule, the amount and a
  // way to reach them are required here and forbidden on the telemetry route.
  // The discriminator is consent, not sensitivity -- see 16-08 vs 16-25.
  matricule?: string;
  amount?: string;
  currency?: string;
  payerPhone?: string;
  appVersion?: string;
};

// A ticket field, ready for a channel that accepts structured input. Prose is
// rendered from these rather than authored separately, so a channel that can
// take fields and one that cannot never disagree about the facts.
export type SupportField = { key: string; label: string; value: string };

export type SupportReport = {
  context: SupportContext;
  subject: string;
  fields: SupportField[];
  // The prose form, for a channel with nothing better than a message body.
  body: string;
};

// The line that separates what the payer writes from what we filled in. The
// machine block sits below it so support can read the payer's own words first
// and a parser can find the facts without guessing.
const DELIMITER = "----------------------------------------";

type Translate = (key: string) => string | undefined;

// i18next echoes the key back when nothing is registered, so a bare
// truthiness check would render "support.field.matricule" into a ticket.
const copy = (translate: Translate, key: string, fallback: string): string => {
  const value = translate(key);
  return value && value !== key ? value : fallback;
};

// Ordered deliberately: the two references first, each under its own heading
// -- conflating them is the defect this line of work exists to remove.
const FIELD_ORDER: { key: keyof SupportFacts; fallback: string }[] = [
  { key: "paymentReference", fallback: "Payment reference" },
  { key: "reference", fallback: "Error reference" },
  { key: "code", fallback: "Error code" },
  { key: "serviceName", fallback: "Service" },
  { key: "matricule", fallback: "Student ID" },
  { key: "amount", fallback: "Amount" },
  { key: "payerPhone", fallback: "Phone" },
  { key: "route", fallback: "Screen" },
  { key: "locale", fallback: "Language" },
  { key: "appVersion", fallback: "App version" },
];

export const buildSupportReport = (
  context: SupportContext,
  facts: SupportFacts,
  translate: Translate
): SupportReport => {
  const amount =
    facts.amount && facts.currency
      ? `${facts.amount} ${facts.currency}`
      : facts.amount;

  const resolved: SupportFacts = { ...facts, amount };

  // A field with no value is ABSENT, never blank. An empty "Amount:" line
  // reads as an amount of nothing rather than as a stage that had none.
  const fields = FIELD_ORDER.reduce<SupportField[]>((acc, { key, fallback }) => {
    const value = resolved[key];
    if (typeof value !== "string" || value === "") return acc;

    return [
      ...acc,
      { key, label: copy(translate, `support.field.${key}`, fallback), value },
    ];
  }, []);

  const subject = copy(
    translate,
    `support.subject.${context}`,
    `SmartCash — ${context}`
  );

  const body = [
    copy(translate, "support.greeting", "Hello,"),
    "",
    copy(translate, `support.reason.${context}`, subject),
    "",
    copy(translate, "support.describe_header", "What happened"),
    copy(
      translate,
      "support.describe_hint",
      "Please describe the problem here."
    ),
    "",
    "",
    DELIMITER,
    copy(translate, "support.do_not_delete", "Please do not delete the lines below."),
    "",
    `[CONTEXT: ${context}]`,
    ...fields.map(field => `${field.label}: ${field.value}`),
  ].join("\n");

  return { context, subject, fields, body };
};

export default buildSupportReport;
